import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import Header from '../../../component/header/header';
import { getSubject } from '../../../api/subject';
import { LoaderComponent } from '../../../utils/Helper';
import {
  SubjectDetail as SubjectDetailData,
  toSubjectDetail,
  enabledDepth,
} from './hierarchyUtils';
import SubjectLevelTabs, { ActiveTab } from './SubjectLevelTabs';
import SubjectOverviewPanel from './SubjectOverviewPanel';
import HierarchyTablePanel from './HierarchyTablePanel';
import '../Subject.style.scss';

const SubjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [subject, setSubject] = useState<SubjectDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('details');

  const loadSubject = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await getSubject(id);
      setSubject(toSubjectDetail(response?.data?.data));
      setError(null);
    } catch (err) {
      setError('Failed to load subject.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSubject();
  }, [loadSubject]);

  const labels = subject?.level_labels ?? [];
  const depth = enabledDepth(labels);

  // If the active level becomes disabled after a label change, fall back.
  useEffect(() => {
    if (typeof activeTab === 'number' && activeTab > depth) {
      setActiveTab(depth >= 1 ? depth : 'details');
    }
  }, [depth, activeTab]);

  if (loading) {
    return (
      <div className="content">
        <LoaderComponent fillPage label="Loading subject…" />
      </div>
    );
  }

  if (error || !subject) {
    return (
      <div className="content">
        <Header
          pageTitle="Subject"
          buttonText="Back to Subjects"
          buttonIcon={<FiArrowLeft />}
          onClick={() => navigate('/subjects')}
        />
        <div className="subject_detail_error">{error || 'Subject not found.'}</div>
      </div>
    );
  }

  return (
    <div className="content subject_detail_page">
      <Header
        pageTitle={subject.title}
        buttonText="Back to Subjects"
        buttonIcon={<FiArrowLeft />}
        onClick={() => navigate('/subjects')}
      />

      <div className="subject_detail_layout">
        <SubjectLevelTabs
          levelLabels={labels}
          enabledDepth={depth}
          activeTab={activeTab}
          onSelect={setActiveTab}
        />

        <div className="subject_detail_content">
          {activeTab === 'details' ? (
            <SubjectOverviewPanel subject={subject} onSubjectChange={setSubject} />
          ) : (
            <HierarchyTablePanel
              key={activeTab}
              subject={subject}
              level={activeTab}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectDetail;
